import { cx } from '@/lib/cx';
import styles from './BarChart.module.css';

/**
 * Vertical bars with the value printed above them (canvas 1l: "Score para o par",
 * "Desempenho por par", "Desempenho por buraco").
 *
 * Laid out with flex rather than SVG, exactly as the canvas does it: the value labels are
 * real text, so they stay crisp, wrap sensibly and are selectable, and the bars stretch to
 * whatever width the column happens to have.
 */

/**
 * `plain` is not part of the severity ramp — it is for charts where the bar's colour carries
 * no meaning and every bar looks the same (the canvas's "Score para o par"). Reaching for
 * `good` there would make the code claim a judgement the chart is not making.
 */
export type BarTone = 'plain' | 'good' | 'even' | 'bad';

export interface Bar {
  key: string;
  /** Signed. Bar length is |value| against the largest magnitude in the set. */
  value: number;
  /** Pre-formatted number shown above the bar. */
  display: string;
  /** Label under the bar. */
  label: string;
  /** Second line under the label — the sample behind the bar. */
  caption?: string;
  tone?: BarTone;
}

/**
 * Severity for a "strokes over par" bar, on the scale the reader has already learned from
 * the distribution legend directly above it on this screen: green is better than bogey,
 * grey is about bogey, red is worse. Reusing that palette means the ramp needs no legend
 * of its own.
 */
export function costTone(overPar: number): BarTone {
  if (overPar < 0.5) return 'good';
  if (overPar <= 1) return 'even';
  return 'bad';
}

const TONE_CLASS: Record<BarTone, string | undefined> = {
  plain: styles.plain,
  good: styles.good,
  even: styles.even,
  bad: styles.bad,
};

export function BarChart({
  bars,
  height = 112,
  referenceValue,
  referenceLabel,
  ariaLabel,
  compact = false,
}: {
  bars: Bar[];
  /** Plot height in px. The canvas uses 104–132 depending on the panel. */
  height?: number;
  /** Draws a dashed horizontal rule, e.g. the mean. */
  referenceValue?: number;
  referenceLabel?: string;
  ariaLabel: string;
  /** Tighter gaps and smaller type, for the 18-bar per-hole chart. */
  compact?: boolean;
}) {
  if (bars.length === 0) return null;

  const values = bars.map((b) => b.value);
  const lowest = Math.min(...values, referenceValue ?? Infinity);
  const highest = Math.max(...values, referenceValue ?? -Infinity);

  /*
   * Bars grow from a zero line. When nothing dips below zero — the common case, and the one
   * the canvas draws — that line *is* the floor and the layout collapses to plain upward
   * bars. A negative value (a hole the player averages under par) splits the plot rather
   * than being silently clipped to nothing.
   *
   * `top` and `bottom` are the two halves' extents, so a bar's length is a share of its own
   * half. Measuring against the combined span instead would shrink every bar as soon as one
   * outlier appeared on the other side of zero.
   */
  const top = Math.max(highest, 0) || 1;
  const bottom = Math.min(lowest, 0);
  const span = top - bottom;
  const zeroPct = (-bottom / span) * 100;

  return (
    <div className={cx(styles.root, compact && styles.compact)}>
      <div className={styles.plot} style={{ height }} role="img" aria-label={ariaLabel}>
        {referenceValue != null && (
          <div
            className={styles.reference}
            style={{ bottom: `${((referenceValue - bottom) / span) * 100}%` }}
          >
            {referenceLabel && <span className={styles.referenceLabel}>{referenceLabel}</span>}
          </div>
        )}

        {bottom < 0 && <div className={styles.zeroLine} style={{ bottom: `${zeroPct}%` }} />}

        {bars.map((bar) => (
          <div key={bar.key} className={styles.column}>
            {bar.value < 0 ? (
              <div
                className={cx(styles.stack, styles.stackDown)}
                style={{ top: `${100 - zeroPct}%`, height: `${zeroPct}%` }}
              >
                <div
                  className={cx(styles.bar, styles.barDown, TONE_CLASS[bar.tone ?? 'plain'])}
                  style={{ height: `${(bar.value / bottom) * 100}%` }}
                />
                <span className={styles.value}>{bar.display}</span>
              </div>
            ) : (
              <div
                className={styles.stack}
                style={{ bottom: `${zeroPct}%`, height: `${100 - zeroPct}%` }}
              >
                <span className={styles.value}>{bar.display}</span>
                <div
                  className={cx(styles.bar, TONE_CLASS[bar.tone ?? 'plain'])}
                  style={{ height: `${(bar.value / top) * 100}%` }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.labels}>
        {bars.map((bar) => (
          <div key={bar.key} className={styles.labelCell}>
            <div className={styles.label}>{bar.label}</div>
            {bar.caption && <div className={styles.caption}>{bar.caption}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
